"use client";

import { useEffect, useState } from "react";
import { getCollationQueue, decideItem, applyAccepted, exportPolicy } from "@/lib/policies/api";
import { useWorkspace } from "@/lib/context/WorkspaceContext";
import { AdverseActionNotice } from "./AdverseActionNotice";
import { strip } from "../theme";

type Kind = "suggestion" | "answer" | "addition";

interface Item {
    id: string;
    kind: Kind;
    ref: string;
    author?: string;
    state: string;
    proposed_text?: string;
    original_text?: string;
    body?: string;
    rationale?: string;
}

function normalizeItems(data: any): Item[] {
    const sugs: Item[] = (data.suggestions || []).map((s: any) => ({
        id: s.id, kind: "suggestion" as Kind, ref: s.sid, author: s.author, state: s.state,
        proposed_text: s.proposed_text, original_text: s.original_text, rationale: s.rationale,
    }));
    const answers: Item[] = (data.answers || []).map((a: any) => ({
        id: a.id, kind: "answer" as Kind, ref: a.p_id, author: a.author, state: a.state, body: a.body,
    }));
    const additions: Item[] = (data.additions || []).map((a: any) => ({
        id: a.id, kind: "addition" as Kind, ref: "New", author: a.author, state: a.state, body: a.body,
    }));
    return [...sugs, ...answers, ...additions];
}

const DECISION_STYLE: Record<string, [string, string, string]> = {
    accepted: ["#d7ead9", "#2e7d4f", "Accepted"],
    rejected: ["#f9d2d3", "#ae232d", "Rejected"],
    needs_discussion: ["#fbe6cf", "#b5651d", "Needs discussion"],
    applied: ["#d9e8ef", "#2f6f86", "Applied to statement"],
};

function ItemRow({ item, onAccept, onDiscuss, onReject }: { item: Item; onAccept: () => void; onDiscuss: () => void; onReject: () => void }) {
    // Accepted/rejected/applied are terminal. "Needs discussion" is deliberately
    // not — it's a parking state, not a decision, so staff can come back after
    // the discussion happens and make the actual call (Accept/Reject).
    const locked = ["accepted", "rejected", "applied"].includes(item.state);
    const showBadge = locked || item.state === "needs_discussion";
    const ds = showBadge ? DECISION_STYLE[item.state] : null;
    const isDiff = item.kind === "suggestion" && item.proposed_text;
    const text = item.proposed_text || item.body || "";

    const btn = (active: boolean, bd: string) => ({
        border: `1px solid ${bd}`,
        background: active ? bd : "#fff",
        color: active ? "#fff" : bd,
    });

    return (
        <div className="border border-[#e6ebe9] rounded-[10px] overflow-hidden">
            <div className="px-4 py-2.5 bg-[#f6f8f7] flex items-center gap-2.5 border-b border-[#eef1ef] flex-wrap">
                <span className="text-[9.5px] font-bold tracking-wide rounded px-1.5 py-0.5 bg-[#eef3f2] text-[#3a6b78]">{item.kind.toUpperCase()}</span>
                <span className="text-[11.5px] font-bold text-[#3a6b78]">{item.ref}</span>
                <span className="text-[11.5px] text-[#88a39b] flex-1">{item.author || "—"}</span>
                {showBadge && ds && (
                    <span style={{ background: ds[0], color: ds[1] }} className="text-[10px] font-bold rounded px-2 py-0.5">{ds[2]}</span>
                )}
            </div>
            <div className="px-4 py-3.5">
                {isDiff ? (
                    <>
                        <div className="text-[11px] font-bold text-[#ae232d] mb-1">ORIGINAL</div>
                        <p className="text-[12.5px] leading-relaxed text-[#8a5b5f] bg-[#fbeef0] rounded-lg px-2.5 py-2 mb-2.5 line-through">{strip(item.original_text)}</p>
                        <div className="text-[11px] font-bold text-[#2c7a63] mb-1">PROPOSED</div>
                        <p className="text-[12.5px] leading-relaxed text-[#1f5a3f] bg-[#e7f3ec] rounded-lg px-2.5 py-2">{strip(text)}</p>
                    </>
                ) : (
                    <p className="text-[12.5px] leading-relaxed text-[#2c3e40]">{strip(text)}</p>
                )}
                {item.rationale && <div className="text-[11.5px] text-[#7c8e90] mt-2.5 italic">Rationale · {item.rationale}</div>}
                {item.state === "needs_discussion" && (
                    <p className="text-[11px] text-[#b5651d] mt-2.5">
                        Parked for discussion — this blocks &quot;Apply accepted&quot; until you come back and Accept or Reject it below.
                    </p>
                )}
                {!locked && (
                    <div className="flex gap-2 mt-3.5">
                        <button onClick={onAccept} style={btn(false, "#2e7d4f")} className="text-xs font-bold rounded-lg px-3.5 py-1.5">Accept</button>
                        <button onClick={onDiscuss} style={btn(item.state === "needs_discussion", "#b5651d")} className="text-xs font-bold rounded-lg px-3.5 py-1.5">Needs discussion</button>
                        <button onClick={onReject} style={btn(false, "#ae232d")} className="text-xs font-bold rounded-lg px-3.5 py-1.5">Reject</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export function CollationModal({ policy, onClose, onApplied }: { policy: any; onClose: () => void; onApplied?: () => void }) {
    const { user } = useWorkspace();
    const [data, setData] = useState<any>(null);
    const [applying, setApplying] = useState(false);
    const [noticeTarget, setNoticeTarget] = useState<Item | null>(null);

    const load = () => getCollationQueue(policy.id).then(setData);
    useEffect(() => { load(); }, [policy.id]);

    const items = data ? normalizeItems(data) : [];
    const acceptedCount = items.filter((i) => i.state === "accepted").length;
    const needsDiscussionCount = items.filter((i) => i.state === "needs_discussion").length;

    const decide = async (item: Item, decision: string, notice?: string) => {
        await decideItem(item.kind, item.id, decision, notice);
        load();
    };

    const reject = (item: Item) => setNoticeTarget(item);

    const confirmReject = async (notice: string) => {
        if (!noticeTarget) return;
        await decide(noticeTarget, "rejected", notice);
        setNoticeTarget(null);
    };

    const apply = async () => {
        setApplying(true);
        try {
            const res = await applyAccepted(policy.id);
            alert(`Change-set applied: ${res.applied} accepted edit(s) written to the live statement text.`);
            load();
            // Apply can flip the policy's lifecycle_state (submitted/in_collation -> applied) —
            // the board behind this modal needs to know so the card moves columns immediately,
            // not only after a manual page refresh.
            onApplied?.();
        } catch (e: any) {
            alert(e.message);
        } finally {
            setApplying(false);
        }
    };

    const download = async (fmt: "json" | "md") => {
        const content = await exportPolicy(policy.id, fmt);
        const blob = new Blob([typeof content === "string" ? content : JSON.stringify(content, null, 2)], { type: "text/plain" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${policy.id}.${fmt}`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const issuerName = user ? `${user.first_name || ""} ${user.last_name || ""}`.trim() || user.email : "Admin";

    return (
        <div className="fixed inset-0 z-[100] bg-black/40 flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
                <div className="px-6 py-5 border-b border-[#eef1ef] flex items-center justify-between sticky top-0 bg-white z-10">
                    <div>
                        <div className="font-bold text-lg text-[#004d57]">{policy.id} · {policy.name}</div>
                        <div className="text-xs text-[#5f7173] mt-0.5">{items.length} items · {acceptedCount} accepted</div>
                    </div>
                    <button onClick={onClose} className="text-xs font-bold uppercase text-gray-400">Close</button>
                </div>

                <div className="p-6 space-y-3.5">
                    {!data ? (
                        <div className="py-8 flex justify-center">
                            <div className="w-6 h-6 border-2 border-[#007a8a]/20 border-t-[#007a8a] rounded-full animate-spin" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="bg-white border border-dashed border-[#cfd9d6] rounded-xl p-10 text-center text-[#88a39b] text-sm">No submitted input awaiting collation.</div>
                    ) : (
                        items.map((it) => (
                            <ItemRow
                                key={`${it.kind}-${it.id}`}
                                item={it}
                                onAccept={() => decide(it, "accepted")}
                                onDiscuss={() => decide(it, "needs_discussion")}
                                onReject={() => reject(it)}
                            />
                        ))
                    )}
                </div>

                {needsDiscussionCount > 0 && (
                    <div className="px-6 py-3 bg-[#fbeef0] border-t border-[#f1d9dc] flex items-start gap-2.5 sticky bottom-[57px]">
                        <span className="text-[#ae232d] font-bold text-sm flex-none">⚠</span>
                        <p className="text-[12px] text-[#8a3a3f] leading-relaxed">
                            <strong>{needsDiscussionCount} item{needsDiscussionCount > 1 ? "s" : ""} still need{needsDiscussionCount > 1 ? "" : "s"} discussion.</strong>{" "}
                            &quot;Apply accepted&quot; is blocked until every parked item above is revisited and Accepted or Rejected.
                        </p>
                    </div>
                )}
                <div className="px-6 py-4 bg-[#f4f6f5] border-t border-[#e6ebe9] flex items-center justify-between sticky bottom-0">
                    <div className="flex gap-2">
                        <button onClick={() => download("md")} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold uppercase">Export .md</button>
                        <button onClick={() => download("json")} className="px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-[11px] font-bold uppercase">Export .json</button>
                    </div>
                    <button
                        onClick={apply}
                        disabled={applying || acceptedCount === 0 || needsDiscussionCount > 0}
                        title={needsDiscussionCount > 0 ? "Resolve every item parked for discussion before applying" : undefined}
                        style={{ background: acceptedCount && needsDiscussionCount === 0 ? "#004d57" : "#cdd9d4" }}
                        className="px-4 py-2 rounded-lg text-white text-xs font-bold uppercase tracking-wide disabled:cursor-not-allowed"
                    >
                        {applying ? "Applying…" : `Apply accepted (${acceptedCount})`}
                    </button>
                </div>
            </div>

            {noticeTarget && (
                <AdverseActionNotice
                    item={{ key: noticeTarget.id, kind: noticeTarget.kind, ref: noticeTarget.ref, author: noticeTarget.author || "—", policy: policy.id, rationale: noticeTarget.rationale }}
                    issuerName={issuerName}
                    onCancel={() => setNoticeTarget(null)}
                    onConfirm={confirmReject}
                />
            )}
        </div>
    );
}
